import React, { useEffect, useState } from "react";
import { Loader2, Play, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import api from "@/lib/api";

/** Read-only parity report between Laaffic CDRs and our Excel-derived
 * ops_call_rows for a picked (team, date-range). No writes. Meant to
 * be run before we build any ingest pipeline so we can confirm field
 * shapes and disposition vocabularies actually line up. */
export default function LaafficParityPage() {
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState({
    team_id: "",
    date_from: _defaultFrom(),
    date_to: _defaultTo(),
    task_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data || [])).catch(() => {});
  }, []);

  const bind = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const run = async () => {
    setLoading(true);
    setErr("");
    setData(null);
    try {
      const params = new URLSearchParams({
        team_id: form.team_id,
        date_from: form.date_from,
        date_to: form.date_to,
      });
      if (form.task_id.trim()) params.set("task_id", form.task_id.trim());
      const r = await api.get(`/admin/integrations/laaffic/parity?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="laaffic-parity-page">
      <div>
        <h1 className="text-2xl font-outfit font-semibold text-slate-900">Laaffic parity report</h1>
        <div className="text-sm text-slate-600 mt-1 max-w-2xl">
          Pull real CDRs from Laaffic and lay them next to your existing Excel-derived
          <code className="mx-1 px-1 py-0.5 bg-slate-100 rounded text-xs">ops_call_rows</code>
          for the same team + window. Read-only — nothing is written. Use it to lock the field mapping
          and the disposition translation table before we go live.
        </div>
        <div className="mt-2 flex gap-2 text-xs">
          <Link to="/admin/integrations" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Credentials
          </Link>
          <Link to="/admin/laaffic-dispositions" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Disposition mapping
          </Link>
        </div>
      </div>

      <div className="glass-card p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500">Team *</label>
            <select className="glass-input w-full h-9 mt-1" required
                    value={form.team_id} onChange={bind("team_id")}
                    data-testid="parity-team">
              <option value="">— pick a team —</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500">From</label>
            <input type="date" className="glass-input w-full h-9 mt-1"
                   value={form.date_from} onChange={bind("date_from")}
                   data-testid="parity-from" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500">To</label>
            <input type="date" className="glass-input w-full h-9 mt-1"
                   value={form.date_to} onChange={bind("date_to")}
                   data-testid="parity-to" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500">Task ID (optional)</label>
            <input className="glass-input w-full h-9 mt-1 font-mono text-xs"
                   value={form.task_id} onChange={bind("task_id")}
                   placeholder="filter to one campaign"
                   data-testid="parity-taskid" />
          </div>
        </div>
        <Button onClick={run}
                disabled={loading || !form.team_id}
                data-testid="parity-run">
          {loading
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</>
            : <><Play className="w-4 h-4 mr-2" /> Run report</>}
        </Button>
      </div>

      {err && (
        <div className="glass-card p-3 border-l-4 border-red-500 text-sm text-red-700"
             data-testid="parity-err">{err}</div>
      )}

      {data && (
        <div className="space-y-6" data-testid="parity-results">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatCard
              label="Laaffic CDRs pulled"
              value={(data.laaffic?.record_count || 0).toLocaleString()}
              sub={
                data.laaffic?.hit_cap
                  ? `⚠ hit safety cap (${(data.laaffic?.max_records || 0).toLocaleString()}) — narrow the date range`
                  : (data.laaffic?.fetch_errors?.length ? "partial (see errors)" : "complete")
              }
            />
            <StatCard label="Our ops_call_rows" value={(data.ours_excel_derived?.row_count || 0).toLocaleString()} sub={`for team ${data.team_id.slice(0, 8)}`} />
            <StatCard label="Date range" value={`${data.date_from} → ${data.date_to}`} />
          </div>

          {data.laaffic?.fetch_errors?.length > 0 && (
            <div className="glass-card p-3 text-xs bg-amber-50 border-l-4 border-amber-400 text-amber-800">
              <b>Laaffic returned an error:</b>
              <pre className="mt-1">{JSON.stringify(data.laaffic.fetch_errors, null, 2)}</pre>
            </div>
          )}

          <section>
            <h2 className="text-sm uppercase tracking-wide text-slate-500 mb-2">Laaffic field shape</h2>
            <div className="glass-card p-0 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr><th className="px-3 py-2">Field name</th><th className="px-3 py-2 text-right">Seen in N records</th></tr>
                </thead>
                <tbody>
                  {(data.laaffic?.field_shape || []).map((f) => (
                    <tr key={f.field} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 font-mono">{f.field}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{f.seen_in}</td>
                    </tr>
                  ))}
                  {(data.laaffic?.field_shape || []).length === 0 && (
                    <tr><td colSpan={2} className="px-3 py-4 text-center text-slate-500">No records — nothing to inspect.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm uppercase tracking-wide text-slate-500">Laaffic dispositions</h2>
                {data.laaffic?.tag_samples && Object.keys(data.laaffic.tag_samples).length > 0 && (
                  <Button
                    size="sm" variant="outline"
                    onClick={() => _downloadTagSamples(data)}
                    data-testid="parity-download-tag-samples"
                  >
                    Download samples JSON
                  </Button>
                )}
              </div>
              <DispTable rows={data.laaffic?.dispositions} tagSamples={data.laaffic?.tag_samples} testid="parity-laaffic-disp" />
            </div>
            <div>
              <h2 className="text-sm uppercase tracking-wide text-slate-500 mb-2">Our Excel dispositions</h2>
              <DispTable rows={data.ours_excel_derived?.dispositions} testid="parity-ours-disp" />
            </div>
          </section>

          <details className="glass-card p-3">
            <summary className="cursor-pointer text-sm font-medium text-slate-700">
              Sample of empty-tag Laaffic records ({data.laaffic?.empty_tag_count ?? 0} total)
              <span className="text-xs text-slate-500 font-normal ml-2">— cross-check these callIds in the Laaffic backoffice to confirm whether tags arrive late</span>
            </summary>
            <pre className="mt-2 text-[10px] bg-slate-50 p-2 rounded overflow-auto max-h-96">
              {JSON.stringify(data.laaffic?.empty_tag_sample || [], null, 2)}
            </pre>
          </details>

          <details className="glass-card p-3">
            <summary className="cursor-pointer text-sm font-medium text-slate-700">
              Sample Laaffic records (first 5 raw)
            </summary>
            <pre className="mt-2 text-[10px] bg-slate-50 p-2 rounded overflow-auto max-h-96">
              {JSON.stringify(data.laaffic?.sample || [], null, 2)}
            </pre>
          </details>

          <details className="glass-card p-3">
            <summary className="cursor-pointer text-sm font-medium text-slate-700">
              Sample of our ops_call_rows (first 5)
            </summary>
            <pre className="mt-2 text-[10px] bg-slate-50 p-2 rounded overflow-auto max-h-96">
              {JSON.stringify(data.ours_excel_derived?.sample || [], null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="glass-card p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-slate-900 mt-0.5 tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function DispTable({ rows, tagSamples, testid }) {
  const [openTag, setOpenTag] = React.useState(null);
  return (
    <div className="glass-card p-0 overflow-hidden" data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-500 text-left">
          <tr>
            <th className="px-3 py-2">Tag</th>
            <th className="px-3 py-2 text-right">Count</th>
            {tagSamples && <th className="px-3 py-2 text-right w-20">&nbsp;</th>}
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => {
            const hasSamples = tagSamples && tagSamples[r.tag]?.length > 0;
            const isOpen = openTag === r.tag;
            return (
              <React.Fragment key={r.tag}>
                <tr className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-mono truncate max-w-xs" title={r.tag}>
                    {r.tag}
                    {r.variants && r.variants.length > 1 && (
                      <span className="ml-2 text-[10px] text-slate-400" title={r.variants.map(v => `${v.tag} (${v.count})`).join(", ")}>
                        [{r.variants.length} casings]
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{r.count.toLocaleString()}</td>
                  {tagSamples && (
                    <td className="px-3 py-1.5 text-right">
                      {hasSamples && (
                        <button
                          onClick={() => setOpenTag(isOpen ? null : r.tag)}
                          className="text-[10px] text-indigo-600 hover:text-indigo-800 underline"
                          data-testid={`tag-view-samples-${r.tag}`}
                        >
                          {isOpen ? "hide" : `view ${tagSamples[r.tag].length}`}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
                {isOpen && hasSamples && (
                  <tr>
                    <td colSpan={3} className="p-0">
                      <pre className="text-[9px] bg-slate-50 p-2 max-h-96 overflow-auto">
                        {JSON.stringify(tagSamples[r.tag], null, 2)}
                      </pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {(rows || []).length === 0 && (
            <tr><td colSpan={tagSamples ? 3 : 2} className="px-3 py-4 text-center text-slate-500">No data.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function _downloadTagSamples(data) {
  const payload = {
    exported_at: new Date().toISOString(),
    team_id: data.team_id,
    date_from: data.date_from,
    date_to: data.date_to,
    total_records: data.laaffic?.record_count,
    empty_tag_count: data.laaffic?.empty_tag_count,
    dispositions: data.laaffic?.dispositions,
    tag_samples: data.laaffic?.tag_samples || {},
    empty_tag_sample: data.laaffic?.empty_tag_sample || [],
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `laaffic_tag_samples_${data.date_from}_${data.date_to}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function _defaultFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 3);
  return d.toISOString().slice(0, 10);
}
function _defaultTo() {
  return new Date().toISOString().slice(0, 10);
}
